<template>
  <div class="wrapper">
    <v-card tile class="overflow-hidden no-scroll-bar pb-3" elevation="0">
      <v-app-bar class="app_bar" fixed color="primary" dark>

        <v-row no-gutters class="w-100 px-2">
          <!-- ▼ 年セレクト -->
          <v-col cols="8" class="pr-1">
            <v-select
              v-model="selectedYear"
              :items="yearList"
              item-title="label"
              item-value="value"
              dense
              hide-details
              class="year-select"
              @update:modelValue="onYearOrMonthChange"
            ></v-select>
          </v-col>

          <!-- ▼ 月セレクト -->
          <v-col cols="4" class="pl-1">
            <v-select
              v-model="selectedMonth"
              :items="monthList"
              dense
              hide-details
              class="month-select"
              item-title="label"
              item-value="value"
              @update:modelValue="onYearOrMonthChange"
            ></v-select>
          </v-col>
        </v-row>

      </v-app-bar>

      <v-expand-transition>
        <v-sheet v-if="isCalendar" class="mt-16 pa-2" elevation="1">
          <div class="calendar-grid-header">
            <div
              v-for="w in weekdayLabels"
              :key="w.label"
              :class="['weekday-label', w.isSunday ? 'text-red' : w.isSaturday ? 'text-blue' : '']"
            >
              {{ w.label }}
            </div>
          </div>

          <div class="calendar-grid-body">
            <div v-for="n in startOffset" :key="'empty-' + n" class="day-cell empty"></div>
            
            <div 
              v-for="day in daysInMonth" 
              :key="day" 
              class="day-cell"
              :class="{ 'is-selected': isSelected(day) }"
              @click="handleDateClick(day)"
            >
              <span class="day-number">{{ day }}</span>
              <div :class="isUnRead(formatDate(day)) ? 'unRead-dot' : 'read-dot'"></div>
            </div>
          </div>
        </v-sheet>
      </v-expand-transition>

      <div class="d-flex justify-center pb-4" />

      <div class="d-flex justify-center pb-2">
        <v-btn text color="grey darken-1" @click="$emit('close')">
          {{ t('common.cancel') }}
        </v-btn>
      </div>

    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import 'dayjs/locale/en';
import 'dayjs/locale/km';
import {
  t,
  locale as appLocale,
  dayjsLocale as appDayjsLocale,
  isJapaneseLocale,
  isKhmerLocale,
} from '@/i18n';
dayjs.locale(appDayjsLocale);

import { chatModule } from '@/store/entityModules/chat';

export default defineComponent({
  name: 'CalendarView',
  props: {
    childId: { type: [Number, String], default: 0 },
    calendar: { type: Array as PropType<string[]>, default: () => [] },
    calendarDate: { type: String, default: '' }
  },
  data() {
    return {
      selectedYear: dayjs().year(),
      selectedMonth: dayjs().month(),
      selectedDate: dayjs().format('YYYY-MM-DD'),
      unReadDate: [] as string[],
      isCalendar: true,

      // ▼ セレクトボックス用
      yearList: [] as { label: string; value: number }[],
    };
  },
  computed: {
    startOffset(): number {
      return dayjs(new Date(this.selectedYear, this.selectedMonth, 1)).day();
    },
    daysInMonth(): number {
      return dayjs(new Date(this.selectedYear, this.selectedMonth, 1)).daysInMonth();
    },
    monthList(): { label: string; value: number }[] {
      return Array.from({ length: 12 }, (_, i) => ({
        label: isJapaneseLocale()
          ? `${i + 1}月`
          : dayjs().month(i).locale(appLocale).format('MMM'),
        value: i,
      }));
    },
    weekdayLabels(): { label: string; isSunday: boolean; isSaturday: boolean }[] {
      const labels = isJapaneseLocale()
        ? ['日', '月', '火', '水', '木', '金', '土']
        : isKhmerLocale()
          ? ['អាទិ', 'ចន្ទ', 'អង្គារ', 'ពុធ', 'ព្រហ', 'សុក្រ', 'សៅរ៍']
          : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return labels.map((label, idx) => ({
        label,
        isSunday: idx === 0,
        isSaturday: idx === 6,
      }));
    },
  },
  methods: {
    t,
    initYearList() {
      const current = dayjs().year();
      const before = 6; // 前6年
      const after = 1;  // 未来1年

      // 合計：6(前) + 1(今年) + 1(未来) = 8要素
      this.yearList = Array.from({ length: before + 1 + after }, (_, i) => {
        const y = current - before + i;
        return {
          label: isJapaneseLocale() ? `${y}年` : `${y}`,
          value: y,
        };
      });
    },

    onYearOrMonthChange() {
      this.fetchCalendarData();
    },

    formatDate(day: number): string {
      return dayjs(new Date(this.selectedYear, this.selectedMonth, day)).format('YYYY-MM-DD');
    },

    async fetchCalendarData() {
      const lastDay = dayjs(new Date(this.selectedYear, this.selectedMonth, 1))
        .endOf('month')
        .date();

      try {
        const response = await chatModule.fetchCalendar({
          childId: Number(this.childId),
          year: this.selectedYear,
          month: this.selectedMonth + 1,
          date: String(lastDay),
        });
        this.unReadDate = Array.isArray(response?.data) ? response.data : [];
      } catch (e) {
        this.unReadDate = [];
      }
    },

    isSelected(day: number): boolean {
      return this.selectedDate === this.formatDate(day);
    },

    isUnRead(dateStr: string): boolean {
      const safeCalendar = Array.isArray(this.calendar) ? this.calendar : [];
      const target = this.unReadDate.length > 0 ? this.unReadDate : safeCalendar;
      return target.includes(dateStr);
    },

    handleDateClick(day: number) {
      this.selectedDate = this.formatDate(day);
      this.$emit('select-date', this.selectedDate);
    },
  },
  watch: {
    calendarDate: {
      handler(newVal) {
        if (!newVal) return;
        const d = dayjs(newVal);
        if (d.isValid()) {
          this.selectedYear = d.year();
          this.selectedMonth = d.month();
          this.selectedDate = d.format('YYYY-MM-DD');
          this.fetchCalendarData();
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.initYearList();
    if (this.calendarDate) {
      const d = dayjs(this.calendarDate);
      if (d.isValid()) {
        this.selectedYear = d.year();
        this.selectedMonth = d.month();
        this.selectedDate = d.format('YYYY-MM-DD');
      }
    }
    this.fetchCalendarData();
  }
});
</script>
<style lang="scss" scoped>
.wrapper {
  height: 100%;
  overflow-y: hidden;

  .app_bar {
    z-index: 10;
    display: flex;
    align-items: center;
  }

  .calendar-grid-header {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    border-bottom: 1px solid #f5f5f5;
  }

  .weekday-label {
    text-align: center;
    font-size: 0.75rem;
    padding: 8px 0;
    font-weight: bold;
  }

  .calendar-grid-body {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    padding: 4px;
  }

  .day-cell {
    aspect-ratio: 1/1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border-radius: 8px;
    transition: background 0.2s;

    &.is-selected {
      background-color: #E3F2FD;
      color: #1976D2;
      font-weight: bold;
    }
  }

  .unRead-dot {
    height: 6px;
    width: 6px;
    background-color: #FF5252;
    border-radius: 50%;
    margin-top: 2px;
  }

  .read-dot {
    height: 6px;
  }

  .cursor-pointer {
    cursor: pointer;
  }

  .year-select,
  .month-select {
    min-width: 0 !important;
    width: 100%;
  }
}
</style>
